// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { Fragment, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { IAppError, IApplicationState, IProject, ErrorCode, IAuth } from "./models/applicationState";
import IAppErrorActions, * as appErrorActions from "./redux/actions/appErrorActions";
import { ErrorHandler } from "./react/components/common/errorHandler/errorHandler";
import { KeyboardManager } from "./react/components/common/keyboardManager/keyboardManager";
import { KeyboardShortcuts } from "./react/components/shell/keyboardShortcuts";
import { MainContentRouter } from "./react/components/shell/mainContentRouter";
import { Sidebar } from "./react/components/shell/sidebar";
import TitleBar from "./react/components/shell/titleBar";

import { getAppInsights } from './services/telemetryService';
import TelemetryProvider from "./providers/telemetry/telemetryProvider";
import IAuthActions, * as authActions from "./redux/actions/authActions";
import firebase from './config/firebase';
import "./App.scss";
import "react-toastify/dist/ReactToastify.css";
import { Spinner, SpinnerSize } from "@fluentui/react";

interface IAppProps {
    currentProject?: IProject;
    appError?: IAppError;
    actions?: IAppErrorActions;
    auth?: IAuth;
    authActions?: IAuthActions;
}

/**
 * @name - App
 * @description - Root level component for VoTT Application
 */
const App = (props: IAppProps) => {
    const { authActions } = props;
    useEffect(() => {
        authActions.setLoading(true);
        const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
            if (user) {
                authActions.setLoading(true);
                await authActions.getUserById(user.uid);
                if (!user.emailVerified) {
                    authActions.setNeedVerification();
                }
            }
            authActions.setLoading(false);
        });

        return () => {
            unsubscribe();
        };
    }, [authActions]);


    useEffect(() => {
        const unsubscribe = firebase.auth().onIdTokenChanged(async (user) => {
            if (user) {
                const idToken = await user.getIdToken();
                authActions.setIdToken(idToken);
            }
        });
        // refresh token every 50 minutes
        const interval = setInterval(async () => {
            try {
                const idToken = await firebase.auth().currentUser.getIdToken(true);
                authActions.setIdToken(idToken);
            } catch {
                // ignore error
            }
        }, 1000 * 60 * 50);
        return () => {
            unsubscribe();
            clearInterval(interval);
        };
    }, [authActions]);


    const platform = global && global.process ? global.process.platform : "web";
    const { user } = props.auth

    try {
        return (
            <Fragment>
                <ErrorHandler
                    error={props.appError}
                    onError={props.actions.showError}
                    onClearError={props.actions.clearError} />
                {/* Don't render app contents during a render error */}
                {(!props.appError || props.appError.errorCode !== ErrorCode.GenericRenderError) &&
                    <KeyboardManager>
                        <BrowserRouter>
                            <TelemetryProvider after={() => { getAppInsights() }}>
                                <div className={`app-shell platform-${platform}`}>
                                    {user && <TitleBar icon="TagGroup">
                                        <div className="app-shortcuts-menu-icon">
                                            <KeyboardShortcuts />
                                        </div>
                                    </TitleBar>}
                                    <div className="app-main">
                                        {user && <Sidebar projectId={props.currentProject?.id} />}
                                        <MainContentRouter />
                                    </div>
                                    <ToastContainer className="frtt-toast-container" role="alert" />
                                </div>
                            </TelemetryProvider>
                        </BrowserRouter>
                    </KeyboardManager>
                }
                {props.auth.loading && <div className="model-loading">
                    <div className="model-loading-spinner">
                        <Spinner size={SpinnerSize.large} />
                    </div>
                </div>}
            </Fragment>
        );
    } catch (error) {
        props.actions.showError({
            errorCode: ErrorCode.GenericRenderError,
            title: error.name,
            message: error.message,
        });
    }
}

function mapStateToProps(state: IApplicationState) {
    return {
        currentProject: state.currentProject,
        appError: state.appError,
        auth: state.auth,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(appErrorActions, dispatch),
        authActions: bindActionCreators(authActions, dispatch)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
